import type { PlayerSettings } from '@cartwhl/3d/player';
import { SceneType } from '@cartwhl/common/types';
import type {
  AsyncState,
  FrameRate,
  Scene,
  SceneTimelineItemFromAPI,
} from '@cartwhl/common/types';

import {
  buildSceneMotionValue,
  parseCompositeSceneMotionValue,
} from '../../shared/motionCompositeValues';
import { resolveCharacterMeshFormat } from '../../shared/characterMeshFormat';
import {
  getBuiltinFreeCharacterMeshPath,
  stripCharacterIdInstanceSuffix,
} from '../../shared/freeCharacterMesh';
import { DEFAULT_CHARACTER, PLAYER_FRAME_RATE } from '../constants';
import type {
  CharacterData,
  MotionEditorActor,
  MotionEditorScene,
  MotionEditorScenePlayback,
  MotionEditorTimelineMotion,
  SelectedSceneMotion,
} from '../types';

export type MotionEditorPlayerPlaybackSettings = Pick<
  PlayerSettings['playback'],
  'frameRate' | 'frameStepSize'
>;

type ResolveMotionEditorPlayerPlaybackSettingsInput = {
  hasRouteSceneID: boolean;
  scene: Pick<AsyncState<MotionEditorScene>, 'status' | 'data'>;
};

export function createMotionEditorSceneFromScene(
  scene: Scene,
  getCharacterData?: (characterID: string) => CharacterData | null,
): MotionEditorScene {
  return {
    sceneID: scene.sceneID,
    sceneName: scene.sceneName,
    type: scene.type,
    objects: Object.fromEntries(
      Object.entries(scene.objects).map(([referenceName, object]) => {
        const characterData =
          getCharacterData?.(object.objectID) ??
          getDefaultCharacterData(object.objectID);

        return [
          referenceName,
          {
            ...object,
            meshURL: object.meshURL ?? characterData?.meshPath,
            rotationOffsets:
              object.rotationOffsets ?? characterData?.rotationOffsets,
            headBoneName: characterData?.headBoneName,
          },
        ];
      }),
    ),
    pendingJobs: scene.pendingJobs,
    sceneSettings: scene.sceneSettings,
    createdAt: scene.createdAt,
    lastUpdatedAt: scene.lastUpdatedAt,
  };
}

export function getScenePlaybackSettings(
  sceneSettings?: MotionEditorScene['sceneSettings'],
): MotionEditorPlayerPlaybackSettings {
  const playbackSettings = sceneSettings?.playbackSettings;

  return {
    frameRate: (playbackSettings?.frameRate ?? PLAYER_FRAME_RATE) as FrameRate,
    frameStepSize: playbackSettings?.frameStepSize ?? 1,
  };
}

export function getDefaultPlayerPlaybackSettings(): MotionEditorPlayerPlaybackSettings {
  return getScenePlaybackSettings();
}

export function resolveMotionEditorPlayerPlaybackSettings(
  input: ResolveMotionEditorPlayerPlaybackSettingsInput,
): MotionEditorPlayerPlaybackSettings | null {
  const { hasRouteSceneID, scene } = input;

  if (!hasRouteSceneID) {
    return getDefaultPlayerPlaybackSettings();
  }

  if (scene.status !== 'ready' || !scene.data) {
    return null;
  }

  return getScenePlaybackSettings(scene.data.sceneSettings);
}

export function arePlayerPlaybackSettingsEqual(
  current: MotionEditorPlayerPlaybackSettings,
  next: MotionEditorPlayerPlaybackSettings,
) {
  return (
    current.frameRate === next.frameRate &&
    current.frameStepSize === next.frameStepSize
  );
}

export function deriveMotionEditorScenePlayback(
  scene: MotionEditorScene,
): MotionEditorScenePlayback {
  return {
    sceneID: scene.sceneID,
    sceneName: scene.sceneName,
    actors: deriveMotionEditorActors(scene),
  };
}

/**
 * Scene objects are stored in DynamoDB as an unordered Map and returned in
 * an arbitrary key order. The server names them "character", "character2",
 * "character3", … matching outputNumber 0, 1, 2, … so we sort numerically
 * before labelling so "Actor 1" always corresponds to body 1 (outputNumber 0)
 * in both the MotionEditor canvas and the batch preview modal.
 */
function sortedSceneObjectEntries(
  objects: MotionEditorScene['objects'],
): [string, MotionEditorScene['objects'][string]][] {
  const referenceNameOrder = (key: string): number => {
    if (key === 'character') return 0;
    const match = /^character(\d+)$/.exec(key);
    return match ? parseInt(match[1]!, 10) - 1 : Number.MAX_SAFE_INTEGER;
  };
  return Object.entries(objects).sort(
    ([a], [b]) => referenceNameOrder(a) - referenceNameOrder(b),
  );
}

export function deriveMotionEditorActors(
  scene: MotionEditorScene,
): MotionEditorActor[] {
  if (scene.type !== SceneType.MOTION || !scene.objects) {
    return [];
  }

  return sortedSceneObjectEntries(scene.objects)
    .map(([referenceName, object], index) => {
      const characterData = object
        ? getDefaultCharacterData(object.objectID)
        : null;
      const meshPath = object?.meshURL ?? characterData?.meshPath;
      const rotationOffsets =
        object?.rotationOffsets ?? characterData?.rotationOffsets;

      if (!object?.objectID || !meshPath) {
        return null;
      }

      const timeline = object.timeline ?? [];
      const timelineMotions = timeline.map((item, timelineIndex) =>
        deriveTimelineMotion(referenceName, item, timelineIndex),
      );

      return {
        referenceName,
        label: `Actor ${index + 1}`,
        characterID: characterData?.characterID ?? object.objectID,
        meshPath,
        meshFormat:
          characterData?.meshFormat ??
          resolveCharacterMeshFormat(meshPath) ??
          undefined,
        identityJsonURL: object.identityJsonURL,
        rotationOffsets,
        headBoneName: object.headBoneName,
        bvhUrls: timeline.flatMap(getTimelineItemBvhUrls),
        timelineMotions,
      } satisfies MotionEditorActor;
    })
    .filter(isNonNullable);
}

export function getFirstMotionEditorActor(
  actors: MotionEditorActor[],
): MotionEditorActor | null {
  return actors[0] ?? null;
}

export function getMotionEditorActorByReferenceName(
  actors: MotionEditorActor[],
  referenceName?: string | null,
): MotionEditorActor | null {
  if (!referenceName) {
    return null;
  }

  return actors.find((actor) => actor.referenceName === referenceName) ?? null;
}

export function getSelectedMotionFromValue(
  actors: MotionEditorActor[],
  motionValue?: string | null,
): SelectedSceneMotion | null {
  if (!motionValue) {
    return null;
  }

  const normalizedMotionValue =
    parseCompositeSceneMotionValue(motionValue)?.motionValue ?? motionValue;

  for (const actor of actors) {
    const selectedMotion = actor.timelineMotions.find(
      (motion) => motion.motionValue === normalizedMotionValue,
    );
    if (selectedMotion) {
      return selectedMotion;
    }
  }

  return null;
}

export function getDefaultSelectedMotion(
  actor?: MotionEditorActor | null,
): SelectedSceneMotion | null {
  return actor?.timelineMotions[0] ?? null;
}

export function getTimelineItemBvhUrls(
  item: SceneTimelineItemFromAPI,
): string[] {
  const stitchedUrls = [item.bvhURL, item.stitchBvhURL].filter(
    isNonEmptyString,
  );
  if (stitchedUrls.length > 0) {
    return stitchedUrls;
  }

  return [item.originalBvhURL].filter(isNonEmptyString);
}

function deriveTimelineMotion(
  referenceName: string,
  item: SceneTimelineItemFromAPI,
  timelineIndex: number,
): MotionEditorTimelineMotion {
  return {
    appliedMotionEditJobID: item.appliedMotionEditJobID ?? null,
    referenceName,
    motionID: item.jobID,
    variationNumber: item.variationNumber,
    timelineIndex,
    title: item.title?.trim() || `Motion ${timelineIndex + 1}`,
    motionValue: buildSceneMotionValue(
      referenceName,
      item.jobID,
      item.variationNumber,
      timelineIndex,
    ),
    bvhURL: item.bvhURL ?? null,
    stitchBvhURL: item.stitchBvhURL ?? null,
    originalBvhURL: item.originalBvhURL ?? null,
    bvhUrls: getTimelineItemBvhUrls(item),
  };
}

function isNonEmptyString(value?: string | null): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function getDefaultCharacterData(characterID: string): CharacterData | null {
  const meshPath = getBuiltinFreeCharacterMeshPath(characterID);
  if (!meshPath) {
    return null;
  }

  return {
    characterID,
    meshPath,
    meshFormat: resolveCharacterMeshFormat(meshPath) ?? undefined,
    rotationOffsets:
      stripCharacterIdInstanceSuffix(characterID) === DEFAULT_CHARACTER.jobID
        ? DEFAULT_CHARACTER.rotationOffsets
        : undefined,
  };
}

function isNonNullable<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}
